import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChatBubble } from '@widget/components/ChatBubble'
import { ChatWindow } from '@widget/components/ChatWindow'
import type { SupportConfig } from '@widget/types'
import { useAuthStore } from '@/stores/auth.store'
import { useSupportChat, PLATFORM_SUPPORT_TENANT_ID } from '@/hooks/useSupportChat'

// Widget styles (scoped r3c-* classes). The dashboard imports them directly
// instead of loading the public widget bundle.
import '@widget/styles/widget.css'

/**
 * Internal support chat for the dashboard.
 *
 * Pure React component: it reuses the public widget's visual components
 * (ChatBubble / ChatWindow) but talks to the r3Converse platform support
 * tenant through the public support endpoints. This avoids the localStorage
 * session collisions the embedded widget caused between tenants.
 *
 * Available to all authenticated users — it is r3Converse's own support
 * channel for its customers, not the support add-on that tenants can buy.
 */
export const SupportChat: React.FC = () => {
  const tenant = useAuthStore((s) => s.tenant)
  const chat = useSupportChat()

  const { data: supportConfig } = useQuery({
    queryKey: ['platform-support-config'],
    queryFn: async () => {
      const response = await fetch(
        `https://api.r3converse.com/api/v1/widgets/config/${PLATFORM_SUPPORT_TENANT_ID}`
      )
      if (!response.ok) return undefined
      const json = await response.json()
      return json?.data?.support as SupportConfig | undefined
    },
    staleTime: 5 * 60 * 1000,
  })

  if (!tenant) return null

  // ChatWindow types `config` as `any`, so a plain object is fine here.
  const config = {
    tenantId: PLATFORM_SUPPORT_TENANT_ID,
    mode: 'support',
    theme: 'dark',
    primaryColor: '#006cd4',
    showPoweredBy: false,
  }

  return (
    <div className="r3c-widget-container r3c-support-chat-dashboard" data-position="bottom-right">
      {chat.isOpen && (
        <ChatWindow
          config={config}
          messages={chat.messages}
          isTyping={chat.isTyping}
          onSendMessage={chat.sendMessage}
          onClose={() => chat.setOpen(false)}
          onClearConversation={chat.clearConversation}
          tenantId={PLATFORM_SUPPORT_TENANT_ID}
          conversationId={chat.conversationId}
          // Dashboard users are authenticated and already accepted the terms at
          // registration — no privacy notice gate needed here.
          privacyNoticeAccepted={true}
          chatMode="support"
          suggestedArticles={chat.suggestedArticles}
          supportConfig={supportConfig}
          onCreateTicket={chat.createTicket}
        />
      )}
      <ChatBubble
        isOpen={chat.isOpen}
        onClick={chat.toggleOpen}
        unreadCount={chat.unreadCount}
        primaryColor={config.primaryColor}
        chatMode="support"
      />
    </div>
  )
}
